import { GridSortDirection } from '@mui/x-data-grid-pro';
import { SortDirection } from './enums';
import {
    ContainerSizeDto,
    DBLCategoryDto,
    DBLSpecificationDto,
    EngineOilTestDto,
    EngineTypeDto,
    ExtendedCompanyRegistrationDto,
    FrequentlyAskedQuestionDto,
    FuelDto,
    InvoiceAmountDto,
    OperatingFluidTypeDto,
    Pageable,
    ProductApprovalDto,
    ProductDto,
    ProductFormulationDto,
    ProductSheetDto,
    ProgramTestBenchDto,
    SaeClassDto,
    SalesAreaDto,
    ServiceAreaDto,
    SheetDto,
    TestBenchResultDto,
    ThirdPartyContractDto,
} from '../api';
import { nameof } from '@plusng/react-common-components';
import { SupportedLanguages } from '../common/i18n/SupportedLanguages';

export const MAX_PAGE_SIZE = 2000;
export const standardPageSizeOptions: number[] = [5, 10, 25, 100];
export const standardSortingOrder: GridSortDirection[] = ['asc', 'desc'];

export const initialPagination: Pageable = {
    page: 0,
    size: 10,
    sort: ['id'],
};

export const getMaxPaginationSortedByAsc = (sort: string): Pageable => {
    return {
        page: 0,
        size: MAX_PAGE_SIZE,
        sort: [`${sort},${SortDirection.ASC}`],
    };
};

export const getMaxOperatingFluidTypePagination = (currentLang: SupportedLanguages) => {
    const paginationProp: keyof OperatingFluidTypeDto = currentLang === SupportedLanguages.de ? 'nameDe' : 'nameEn';
    return getMaxPaginationSortedByAsc(nameof<OperatingFluidTypeDto>(paginationProp));
};

export const getMaxSheetPagination = () => {
    return getMaxPaginationSortedByAsc(nameof<SheetDto>('sheetNumber'));
};

export const getMaxProductSheetPagination = () => {
    return getMaxPaginationSortedByAsc(nameof<ProductSheetDto>('id'));
};

export const getMaxSalesAreasPagination = (currentLang: SupportedLanguages) => {
    const paginationProp: keyof SalesAreaDto = currentLang === SupportedLanguages.de ? 'nameDe' : 'nameEn';
    return getMaxPaginationSortedByAsc(nameof<SalesAreaDto>(paginationProp));
};

export function getMaxFaqPagination(langState: SupportedLanguages) {
    const sortByProp: keyof FrequentlyAskedQuestionDto = langState === SupportedLanguages.de ? 'titleDe' : 'titleEn';
    return getMaxPaginationSortedByAsc(nameof<FrequentlyAskedQuestionDto>(sortByProp));
}

export function getMaxContainerSizesPagination(langState: SupportedLanguages) {
    const sortByProp: keyof ContainerSizeDto = langState === SupportedLanguages.de ? 'nameDe' : 'nameEn';
    return getMaxPaginationSortedByAsc(nameof<ContainerSizeDto>(sortByProp));
}

export function getMaxEngineTypesPagination() {
    return getMaxPaginationSortedByAsc(nameof<EngineTypeDto>('name'));
}

export function getMaxTestStandsPagination() {
    return getMaxPaginationSortedByAsc(nameof<ProgramTestBenchDto>('name'));
}

export function getMaxSaeClassesPagination() {
    return getMaxPaginationSortedByAsc(nameof<SaeClassDto>('className'));
}

export function getMaxFuelsPagination() {
    return getMaxPaginationSortedByAsc(nameof<FuelDto>('name'));
}

export function getMaxInvoiceAmountsPagination() {
    return getMaxPaginationSortedByAsc(nameof<InvoiceAmountDto>('amount'));
}

export function getMaxTestBenchResultPagination() {
    return getMaxPaginationSortedByAsc(nameof<TestBenchResultDto>('reason'));
}

export function getMaxProductFormulationPagination() {
    return getMaxPaginationSortedByAsc(nameof<ProductFormulationDto>('formulation'));
}

export function getMaxEngineOilTestPagination() {
    return getMaxPaginationSortedByAsc(nameof<EngineOilTestDto>('engineNumber'));
}

export function getMaxCompanyRegistrationsPagination() {
    return getMaxPaginationSortedByAsc(nameof<ExtendedCompanyRegistrationDto>('name'));
}

export function getMaxServiceAreasPagination(langState: SupportedLanguages) {
    const sortByProp: keyof ServiceAreaDto = langState === SupportedLanguages.de ? 'titleDe' : 'titleEn';
    return getMaxPaginationSortedByAsc(nameof<ServiceAreaDto>(sortByProp));
}

export function getMaxDblSpecificationsPagination(langState: SupportedLanguages) {
    const sortByProp: keyof DBLSpecificationDto = langState === SupportedLanguages.de ? 'nameDe' : 'nameEn';
    return getMaxPaginationSortedByAsc(nameof<DBLSpecificationDto>(sortByProp));
}

export function getMaxDblCategoryPagination(langState: SupportedLanguages) {
    const sortByProp: keyof DBLCategoryDto = langState === SupportedLanguages.de ? 'nameDe' : 'nameEn';
    return getMaxPaginationSortedByAsc(nameof<DBLCategoryDto>(sortByProp));
}

export function getMaxProductApprovalPagination() {
    return getMaxPaginationSortedByAsc(nameof<ProductApprovalDto>('approvalId'));
}

export function getMaxThirdPartyContractPagination() {
    return getMaxPaginationSortedByAsc(nameof<ThirdPartyContractDto>('id'));
}
export function getMaxProductPagination() {
    return getMaxPaginationSortedByAsc(nameof<ProductDto>('name'));
}
